import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, UserCircle, Users, Contact, MessageSquare, HelpCircle, Inbox,
  Bookmark, Building2, Banknote, Calendar, BookOpen, BarChart3, Activity,
  Settings, Shield, Menu, Search, Home, AtSign, Flag, Megaphone, MessageCircle,
  Sparkles, ChevronDown, Users2, BadgeCheck,
} from "lucide-react";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import CommandPalette from "@/components/layout/CommandPalette";
import { useAuth } from "@/context/AuthContext";
import { useAnalyticsConfig } from "@/hooks/useAnalyticsData";
import useCrmConfig from "@/hooks/useCrmConfig";
import useGroupsConfig from "@/hooks/useGroupsConfig";
import useGroupsPendingRequests from "@/hooks/useGroupsPendingRequests";
import { useRealtime, useRealtimeEvent } from "@/context/RealtimeContext";
import { NotificationBell } from "@/components/social/NotificationBell";
import { MessagesNavButton } from "@/components/social/MessagesNavButton";
import { useStaffMessaging } from "@/hooks/useStaffMessaging";
import { UserMenu } from "@/components/layout/UserMenu";
import api from "@/lib/api";
import { MobileNavTabs } from "@/components/layout/MobileBottomNav";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/account", label: "Account Settings", icon: Settings },
  { to: "/dashboard/profile", label: "My Profile", icon: UserCircle },
  { to: "/dashboard/network", label: "Network", icon: Users },
  // Relationship CRM Phase 2: rendered ONLY when the safe CRM config says this member can
  // see it (rollout flag), and the routes/API deny access independently of the sidebar.
  { to: "/dashboard/contacts", label: "Contacts", icon: Contact, crm: true },
  // Groups Batch B: the DASHBOARD entry is personal Group management — My groups,
  // Pending and Drafts (`/dashboard/groups`). Public Group DISCOVERY is a primary
  // destination in the main navigation at `/groups`; these are different jobs, not
  // duplicate navigation. Rendered only when the safe Groups config says this member can
  // see it; the routes and API deny access independently of the sidebar.
  { to: "/dashboard/groups", label: "My Groups", icon: Users2, groups: true },
  { to: "/dashboard/noted", label: "Noted", icon: AtSign },
  { to: "/dashboard/messages", label: "Messages", icon: MessageSquare },
  // Staff Messaging V1 — the shared official Startup Otter inbox. Rendered only for
  // authorized staff; the route and API deny access independently of the sidebar.
  { to: "/dashboard/staff-inbox", label: "Staff Inbox", icon: BadgeCheck, staffMessaging: true },
  // Otter Assist V1 Phase 6 (OAD-072): the PERMANENT Engagement group. Inquiries and Questions
  // keep their existing routes, behaviour and permissions — only their navigation placement
  // changes — and Otter Assist joins them here. There is no temporary standalone nav item.
  { group: "engagement", label: "Engagement", icon: Sparkles },
  { to: "/dashboard/bookmarks", label: "Bookmarks", icon: Bookmark },
  { to: "/dashboard/businesses", label: "Businesses", icon: Building2 },
  { to: "/dashboard/capital-profiles", label: "Capital Profiles", icon: Banknote },
  { to: "/dashboard/events", label: "Events", icon: Calendar },
  { to: "/dashboard/resources", label: "Resources", icon: BookOpen },
  { to: "/dashboard/analytics", label: "Profile Analytics", icon: BarChart3 },
  { to: "/dashboard/activity", label: "Activity Feed", icon: Activity },
];

const ENGAGEMENT_ITEMS = [
  { to: "/dashboard/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/dashboard/questions", label: "Questions", icon: HelpCircle },
  { to: "/dashboard/otter-assist", label: "Otter Assist", icon: Sparkles },
];

const SECTION_BY_PATH = {
  "/dashboard/network": "network",
  "/dashboard/questions": "questions",
  "/dashboard/inquiries": "inquiries",
  "/dashboard/bookmarks": "bookmarks",
  "/dashboard/noted": "noted",
  "/dashboard/businesses": "businesses",
  "/dashboard/capital-profiles": "capital",
  "/dashboard/profile": "profile",
  "/dashboard/events": "events",
  "/dashboard/resources": "resources",
  "/dashboard/admin/moderation": "moderation",
};

// Quick links to the PUBLIC directory pages — shown only on the dashboard topbar.
const PUBLIC_QUICKLINKS = [
  { key: "chatter", to: "/chatter", label: "Chatter", icon: MessageCircle },
  { key: "people", to: "/people", label: "People", icon: Users },
  { key: "businesses", to: "/businesses", label: "Businesses", icon: Building2 },
  { key: "capital", to: "/capital", label: "Capital", icon: Banknote },
  { key: "events", to: "/events", label: "Events", icon: Calendar },
  { key: "resources", to: "/resources", label: "Resources", icon: BookOpen },
];


const ITEM_CLASS = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${
    isActive ? "bg-teal-50 text-teal-700 border-l-2 border-l-primary"
      : "text-foreground/70 hover:bg-secondary hover:text-foreground border-l-2 border-l-transparent"}`;

/**
 * Otter Assist V1 Phase 6 (OAD-071 / OAD-072) — the permanent Engagement group.
 *
 * The unread Community Needs count sits on **Otter Assist**, and the parent **Engagement** item
 * shows an attention dot whenever something inside needs attention, so a collapsed menu can never
 * hide new Community Needs. Inquiries and Questions are unchanged apart from their placement.
 */
const EngagementGroup = ({ onNavigate, markSectionRead, sectionCounts = {} }) => {
  const location = useLocation();
  const insideGroup = ENGAGEMENT_ITEMS.some((i) => location.pathname.startsWith(i.to));
  const [open, setOpen] = useState(insideGroup);
  const { data: unreadData } = useQuery({
    queryKey: ["oa-unread-count"],
    queryFn: async () => (await api.get("/otter-assist/community-needs/unread-count")).data,
    retry: false,
    refetchInterval: 120000,
  });
  const assistUnread = unreadData?.unread || 0;
  const countFor = (to) => {
    if (to === "/dashboard/otter-assist") return assistUnread;
    return sectionCounts[SECTION_BY_PATH[to]] || 0;
  };
  const needsAttention = ENGAGEMENT_ITEMS.some((i) => countFor(i.to) > 0);

  return (
    <div data-testid="sidebar-engagement-group">
      <button type="button" onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="sidebar-engagement"
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors border-l-2 ${
          insideGroup ? "border-l-primary bg-teal-50/60 text-teal-700"
            : "border-l-transparent text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
        <Sparkles className="h-[18px] w-[18px]" />
        <span className="flex-1 text-left">Engagement</span>
        {!open && needsAttention && (
          <span className="h-2 w-2 rounded-full bg-primary" data-testid="sidebar-engagement-attention" />
        )}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border pl-2">
          {ENGAGEMENT_ITEMS.map((item) => {
            const sec = SECTION_BY_PATH[item.to];
            const count = countFor(item.to);
            return (
              <NavLink key={item.to} to={item.to}
                onClick={() => { if (sec && markSectionRead) markSectionRead(sec); if (onNavigate) onNavigate(); }}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className={ITEM_CLASS}>
                <item.icon className="h-[18px] w-[18px]" />
                <span className="flex-1">{item.label}</span>
                {count > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground"
                    data-testid={item.to === "/dashboard/otter-assist"
                      ? "sidebar-badge-otter-assist" : `sidebar-badge-${sec}`}>
                    {count > 9 ? "9+" : count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};


const NavList = ({ onNavigate, isAdmin, isModerator }) => {
  const { messageUnread = 0, sectionCounts = {}, markSectionRead } = useRealtime();
  // Analytics Portal V1 §12.1: the sidebar entry is renamed to "Analytics" ONLY when the safe
  // config says the portal is enabled and visible to this viewer. While it is disabled the
  // existing "Profile Analytics" entry and its legacy page are left exactly as they were.
  const { portalActive: analyticsPortalActive } = useAnalyticsConfig();
  const { visible: crmVisible } = useCrmConfig();
  const { visible: groupsVisible } = useGroupsConfig();
  // Staff Messaging V1 — capability probe for the Staff Inbox entry + its shared unread badge.
  const { canStaffMessage } = useStaffMessaging();
  const { data: staffUnread, refetch: refetchStaffUnread } = useQuery({
    queryKey: ["staff-messaging-unread"],
    queryFn: async () => (await api.get("/staff-messaging/unread-count")).data,
    enabled: !!canStaffMessage,
    refetchInterval: 60_000,
    retry: false,
  });
  // Staff Messaging V1 — keep the shared badge live instead of waiting for the next poll:
  // a member's reply, a teammate opening a thread, or a recipient read all move the count.
  useRealtimeEvent((evt) => {
    if (!canStaffMessage) return;
    if (["staff_message", "staff_thread_read", "staff_messages_read"].includes(evt?.type)) {
      refetchStaffUnread();
    }
  });
  // GRP-DEC-312: membership requests awaiting THIS member's decision, aggregated across
  // every Group they organize. It rides the existing "My Groups" item — no new nav entry.
  const { total: groupsPending } = useGroupsPendingRequests({ enabled: groupsVisible });
  const navItems = (analyticsPortalActive
    ? NAV.map((item) => (item.to === "/dashboard/analytics"
      ? { ...item, label: "Analytics" } : item))
    : NAV).filter((item) => (!item.crm || crmVisible) && (!item.groups || groupsVisible)
      && (!item.staffMessaging || canStaffMessage));
  // SEC-12: pending team invitations (self-only endpoint, shared/cached query) drive a
  // persistent badge on Businesses / Capital Profiles even after the notification is read.
  const { data: invData } = useQuery({
    queryKey: ["my-team-invitations"],
    queryFn: async () => (await api.get("/team-invites/mine")).data,
  });
  const invites = invData?.invitations || [];
  const pendingInvites = {
    businesses: invites.filter((i) => i.kind === "business").length,
    capital: invites.filter((i) => i.kind === "capital").length,
  };
  const badgeFor = (to) => {
    if (to === "/dashboard/messages") return messageUnread;
    if (to === "/dashboard/staff-inbox") return staffUnread?.unread || 0;
    if (to === "/dashboard/groups") return groupsPending;
    const sec = SECTION_BY_PATH[to];
    if (!sec) return 0;
    const base = sectionCounts[sec] || 0;
    // Don't double-count the invite's own unread notification (it also feeds `base`).
    if (sec === "businesses" || sec === "capital") return Math.max(base, pendingInvites[sec] || 0);
    return base;
  };
  return (
  <nav className="space-y-0.5">
    {isAdmin && (
      <NavLink to="/dashboard/admin" onClick={onNavigate} data-testid="sidebar-admin"
        className={({ isActive }) => `mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${isActive ? "bg-teal-50 text-teal-700 border-l-2 border-l-primary" : "text-foreground/70 hover:bg-secondary border-l-2 border-l-transparent"}`}>
        <Shield className="h-[18px] w-[18px]" /> Admin
      </NavLink>
    )}
    {navItems.map((n) => {
      if (n.group === "engagement") {
        return <EngagementGroup key="engagement" onNavigate={onNavigate}
          markSectionRead={markSectionRead} sectionCounts={sectionCounts} />;
      }
      const sec = SECTION_BY_PATH[n.to];
      const count = badgeFor(n.to);
      return (
      <NavLink
        key={n.to}
        to={n.to}
        end={n.end}
        onClick={() => { if (sec && markSectionRead) markSectionRead(sec); if (onNavigate) onNavigate(); }}
        data-testid={`sidebar-${n.label.toLowerCase().replace(/ /g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${
            isActive ? "bg-teal-50 text-teal-700 border-l-2 border-l-primary" : "text-foreground/70 hover:bg-secondary hover:text-foreground border-l-2 border-l-transparent"
          }`
        }
      >
        <n.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{n.label}</span>
        {count > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground" data-testid={n.to === "/dashboard/messages" ? "sidebar-messages-badge"
              : n.to === "/dashboard/groups" ? "sidebar-badge-groups"
              : n.to === "/dashboard/staff-inbox" ? "sidebar-badge-staff-inbox" : `sidebar-badge-${sec}`}>{count > 9 ? "9+" : count}</span>
        )}
      </NavLink>
      );
    })}
    {isAdmin && (
      <NavLink to="/dashboard/marketing" onClick={onNavigate} data-testid="sidebar-marketing"
        className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${isActive ? "bg-teal-50 text-teal-700 border-l-2 border-l-primary" : "text-foreground/70 hover:bg-secondary border-l-2 border-l-transparent"}`}>
        <Megaphone className="h-[18px] w-[18px]" /> Marketing
      </NavLink>
    )}
    {!isAdmin && isModerator && (
      <NavLink to="/dashboard/admin/moderation" onClick={() => { if (markSectionRead) markSectionRead("moderation"); if (onNavigate) onNavigate(); }} data-testid="sidebar-moderation"
        className={({ isActive }) => `mt-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-[500] transition-colors ${isActive ? "bg-teal-50 text-teal-700 border-l-2 border-l-primary" : "text-foreground/70 hover:bg-secondary border-l-2 border-l-transparent"}`}>
        <Flag className="h-[18px] w-[18px]" /> <span className="flex-1">Moderation</span>
        {(sectionCounts.moderation || 0) > 0 && (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground" data-testid="sidebar-badge-moderation">{sectionCounts.moderation > 9 ? "9+" : sectionCounts.moderation}</span>
        )}
      </NavLink>
    )}
  </nav>
  );
};

export default function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-[260px] shrink-0 flex-col border-r border-border bg-[hsl(var(--card)/0.6)] backdrop-blur-xl lg:flex">
        <div className="flex h-16 shrink-0 items-center px-5"><Logo /></div>
        <div className="flex-1 overflow-y-auto px-3 pb-6 pt-2"><NavList isAdmin={user?.role === "admin"} isModerator={user?.role === "moderator"} /></div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header data-so-chrome className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-[hsl(var(--card)/0.72)] px-4 backdrop-blur-xl sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border lg:hidden" data-testid="app-mobile-menu"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-[280px] flex-col p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex h-16 shrink-0 items-center px-5"><Logo /></div>
              <div className="flex-1 overflow-y-auto px-3 pb-6"><NavList onNavigate={() => setMobileOpen(false)} isAdmin={user?.role === "admin"} isModerator={user?.role === "moderator"} /></div>
            </SheetContent>
          </Sheet>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="group relative hidden h-9 max-w-md flex-1 items-center gap-2 rounded-lg border border-border bg-[hsl(var(--card)/0.6)] px-3 text-sm text-muted-foreground backdrop-blur-sm transition-colors hover:text-foreground sm:flex"
            data-testid="global-search-trigger"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">Search people, businesses, capital…</span>
            <kbd className="pointer-events-none hidden items-center gap-0.5 rounded border border-border bg-secondary px-1.5 font-mono text-[10px] font-[600] text-muted-foreground md:inline-flex">⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" className="sm:hidden" onClick={() => setPaletteOpen(true)} data-testid="global-search-trigger-mobile"><Search className="h-4 w-4" /></Button>
            <MessagesNavButton />
            <NotificationBell />
            <Button variant="ghost" size="sm" onClick={() => navigate("/home")} data-testid="topbar-home-button"><Home className="h-4 w-4" /></Button>
            {/* Circular section shortcuts: desktop only. Below lg the same six links are
                already in the sticky MobileNavTabs strip, so showing both duplicated the
                nav on tablet portrait (reported 2026-08-18). */}
            <div className="hidden items-center gap-1.5 lg:flex" data-testid="topbar-public-links">
              {PUBLIC_QUICKLINKS.map((l) => (
                <Link key={l.key} to={l.to} title={l.label} data-testid={`topbar-nav-${l.key}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-teal-50 text-teal-700 transition-colors hover:bg-teal-100">
                  <l.icon className="h-4 w-4" />
                </Link>
              ))}
            </div>
            <UserMenu />
          </div>
        </header>

        <MobileNavTabs />

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
